import { Box, LinearProgress, SxProps, Typography } from '@mui/material';
import SectionLoader from 'components/shared/SectionLoader';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import { formatMetrics, MetricType } from './data';
import { usePlatformInsights } from './usePlatformInsights';
import BusinessInsightsReport from 'components/featured/Reports/BusinessInsightsReport';

const PlatformInsights = () => {
  const { id } = useParams();
  const { data, loading } = usePlatformInsights(id ?? '');

  if (loading || !data) return <SectionLoader />;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        height: 'calc(100vh - 290px)',
        overflow: 'scroll',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '24px',
        }}
      >
        {Object.entries(data.metrics).map(([key, value]) => {
          const k = key as MetricType;
          const textColor = k === 'successRate' ? zeplinColor.Success : zeplinColor.BasicBlack;

          return (
            <Box
              key={key}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                padding: '16px',
                border: `1px solid ${zeplinColor.Neutral5}`,
                borderRadius: '16px',
                gap: '12px',
              }}
            >
              <Typography variant='responsiveDisplay5' color={textColor}>
                {formatMetrics(k, value)}
              </Typography>
              <Typography variant='responsiveBody3'>{Utilities.camelCaseToTitleCase(k)}</Typography>
            </Box>
          );
        })}
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px',
          border: `1px solid ${zeplinColor.Neutral5}`,
          borderRadius: '16px',
          gap: '12px',
        }}
      >
        <Typography variant='responsiveHeading3'>Fit Recommendations</Typography>

        {data.fitRecommendations.length
          ? data.fitRecommendations.map((item) => {
              const { clientId, clientName, score, reasons } = item;

              return (
                <Box
                  key={clientId}
                  sx={{
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'space-between',
                    padding: '16px',
                    borderRadius: '16px',
                    gap: '12px',
                    bgcolor: zeplinColor.Neutral3,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <ClientAvatar
                      clientName={clientName}
                      profilePicture=''
                      style={{
                        width: '44px',
                        height: '44px',
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                      }}
                    >
                      <Typography variant='responsiveHeading4'>
                        {Utilities.capitalize(clientName)}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '8px',
                          color: zeplinColor.Neutral10,
                        }}
                      >
                        {reasons.map((reason) => (
                          <Typography
                            key={reason}
                            variant='responsiveBody3'
                            sx={{
                              ':not(:last-child)::after': {
                                content: '""',
                                display: 'inline-block',
                                height: '5px',
                                width: '5px',
                                marginLeft: '8px',
                                borderRadius: '50%',
                                verticalAlign: 'middle',
                                backgroundColor: zeplinColor.Neutral10,
                              },
                            }}
                          >
                            {Utilities.capitalize(reason)}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      color: zeplinColor.Success,
                    }}
                  >
                    <LinearProgress
                      variant='determinate'
                      value={score}
                      sx={{
                        minWidth: '160px',
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: zeplinColor.Neutral4,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 5,
                          backgroundColor: zeplinColor.Success,
                        },
                      }}
                    />
                    {score}
                  </Box>
                </Box>
              );
            })
          : 'No fit recommendations'}
      </Box>
      <Box
  sx={{
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${zeplinColor.Neutral5}`,
    gap: '3px',
    flexShrink: 0,
    backgroundColor: '#FFFFFF',
  }}
> 
  <Typography 
    variant="responsiveHeading3" 
    sx={{ color: zeplinColor.Neutral1, fontWeight: 700 }}
  >
    Deal Velocity Trend
  </Typography>
  
  <Box sx={{ width: '100%', height: '700px', position: 'relative' }}>
    <BusinessInsightsReport 
      reportName="Report - Business Insights"
      businessId={id} 
    />
  </Box>
</Box>
    </Box>
  );
};

export default PlatformInsights;

const ClientAvatar = ({
  profilePicture,
  clientName,
  style = { width: '24px', height: '24px' },
}: {
  profilePicture?: string;
  clientName?: string;
  style?: SxProps;
}) => {
  const [imageError, setImageError] = useState(false);

  return (
    <Box
      sx={{
        ...style,
        borderRadius: '50%',
        overflow: 'hidden',
        flexShrink: 0,
        backgroundColor: zeplinColor.Brand1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {profilePicture && !imageError ? (
        <img
          src={profilePicture}
          alt=''
          width='100%'
          height='100%'
          style={{ objectFit: 'cover' }}
          onError={() => setImageError(true)}
        />
      ) : clientName ? (
        <Typography
          variant='body2'
          sx={{
            color: zeplinColor.Neutral1,
            fontSize: '12px',
            fontWeight: 600,
            lineHeight: '24px',
          }}
        >
          {clientName.charAt(0).toUpperCase()}
        </Typography>
      ) : null}
    </Box>
  );
};
